/**
 * Pagina /tesoreria/pagos — Sprint 13 S13-04 (migrada al portal
 * desde apps/frontend-superadmin tras la doctrina Portal vs
 * Superadmin del 2026-05-13).
 *
 * Replica `calbucura-design-handoff/reference-pages/03-tesorero-pagos-distribuir.html`.
 *
 * Original: Sprint 12 S12-03 (listado + StatCards + filtros) +
 * S12-04 (modal de clasificacion multi-concepto). Ahora vive en el
 * portal bajo Vista Tesoreria (TesoreriaLayout con sidebar).
 *
 * Distincion auto-match vs manual basada en `lote_id`:
 * - `lote_id != null` → Casa identificada (borde verde). El
 *   auto-detector reconocio la Casa pero no pudo distribuir (D4
 *   estricto del Sprint 7).
 * - `lote_id == null` → Sin identificar (borde warning). Ni la Casa se
 *   pudo determinar; requiere clasificacion 100% manual.
 *
 * RBAC: la ruta usa `requireRole='tesorero'` y depende del gate de
 * dominio `tipo_cuenta='operativa'` en el ProtectedRoute (S13-01).
 * La cuenta institucional NO accede aqui — vive solo en
 * sa.calbucura.cl.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { ApiError } from '../../lib/api/client';
import {
  listPagos,
  revertirAprobacionPago,
  getPagosAutoRecientesCount,
  type AutoRecientesCount,
} from '../../lib/api/pagos';
import type { EstadoPago, PagoRead } from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { formatFecha } from '../../lib/format';
import { Monto, Num } from '../../components/tesoreria/Monto';
import { ModalClasificar } from './ModalClasificar';
import { ModalRegistrarPagoManual } from '../../components/tesoreria/ModalRegistrarPagoManual';
import { CorreosBancoTab } from '../../components/tesoreria/CorreosBancoTab';

type TipoFiltro = 'todas' | 'casa_identificada' | 'sin_identificar';
type EstadoTab = Extract<EstadoPago, 'por_distribuir' | 'aprobado'>;

interface StatItem {
  label: string;
  value: ReactNode;
  meta?: string;
  valueColor?: 'warning' | 'success' | 'orange' | 'default';
}

function Estadistica({ item }: { item: StatItem }) {
  const colorClass =
    item.valueColor === 'warning'
      ? 'text-state-warning'
      : item.valueColor === 'success'
      ? 'text-state-success'
      : item.valueColor === 'orange'
      ? 'text-orange'
      : 'text-text-primary';
  return (
    <div className="bg-bg-card border-[0.5px] border-white/[0.08] rounded-md p-4">
      <div className="text-xs uppercase tracking-wide text-text-secondary font-medium">
        {item.label}
      </div>
      <div className={`text-2xl font-medium mt-1 ${colorClass}`}>{item.value}</div>
      {item.meta && (
        <div className="text-xs text-text-muted mt-1">{item.meta}</div>
      )}
    </div>
  );
}

interface FilaPagoProps {
  pago: PagoRead;
  modo: 'clasificar' | 'reasignar';
  onAccion: (pago: PagoRead) => void;
  loading?: boolean;
}

function FilaPago({ pago, modo, onAccion, loading }: FilaPagoProps) {
  const tieneLote = pago.lote_id !== null;
  const esAprobado = modo === 'reasignar';
  const borderClass = esAprobado
    ? 'border-l-[3px] border-l-state-info'
    : tieneLote
    ? 'border-l-[3px] border-l-state-success'
    : 'border-l-[3px] border-l-state-warning';
  const montoColor = esAprobado
    ? 'text-state-info'
    : tieneLote
    ? 'text-state-success'
    : 'text-state-warning';

  return (
    <div
      className={`bg-bg-card border-[0.5px] border-white/[0.08] ${borderClass} rounded-md p-4 mb-2 grid grid-cols-[1fr_auto] gap-4 items-center`}
      data-testid="fila-pago"
    >
      <div className="flex flex-col gap-1 min-w-0">
        <div className="flex gap-2 items-center flex-wrap">
          {esAprobado ? (
            <>
              <span className="font-medium text-text-primary">
                {pago.lote_identificador
                  ? `Casa ${pago.lote_identificador}`
                  : 'Sin casa'}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-[2px] bg-state-info/15 text-state-info rounded-xs text-xs font-medium">
                Aprobado
              </span>
              {pago.aprobado_automaticamente && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-[2px] bg-orange/15 text-orange rounded-xs text-xs font-medium"
                  title="Procesado automáticamente por el sistema (auto-distribución), no manualmente"
                >
                  Automático
                </span>
              )}
            </>
          ) : tieneLote ? (
            <>
              <span className="font-medium text-text-primary">
                {pago.lote_identificador
                  ? `Casa ${pago.lote_identificador}`
                  : `Casa #${pago.lote_id!.slice(0, 8)}`}
              </span>
              <span className="inline-flex items-center gap-1 px-2 py-[2px] bg-state-success/15 text-state-success rounded-xs text-xs font-medium">
                Casa identificada
              </span>
            </>
          ) : (
            <>
              <span className="font-medium text-state-warning">Sin identificar</span>
              <span className="inline-flex items-center gap-1 px-2 py-[2px] bg-state-warning/15 text-state-warning rounded-xs text-xs font-medium">
                Manual requerido
              </span>
            </>
          )}
        </div>
        <div className="text-sm text-text-secondary">
          Remitente: {pago.remitente_nombre} · {pago.email_template_detectado} ·{' '}
          {formatFecha(pago.fecha_transferencia)}
        </div>
        {pago.mensaje_email && pago.mensaje_email.trim() !== '' && (
          <div className="text-xs text-text-muted truncate max-w-xl">
            Mensaje: "{pago.mensaje_email}"
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-2">
        <div className={`text-2xl font-medium ${montoColor}`}>
          +<Monto value={pago.monto_total} />
        </div>
        <Button
          variant={esAprobado ? 'secondary' : 'primary'}
          size="sm"
          onClick={() => onAccion(pago)}
          disabled={loading}
        >
          {esAprobado ? 'Reasignar' : 'Clasificar →'}
        </Button>
      </div>
    </div>
  );
}

export function PagosPage() {
  const [estadoTab, setEstadoTab] = useState<EstadoTab>('por_distribuir');
  // Tab "Correos del banco" (ingesta): convive con los tabs de estado de pago.
  const [mostrarCorreos, setMostrarCorreos] = useState(false);
  const [pagos, setPagos] = useState<PagoRead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [tipo, setTipo] = useState<TipoFiltro>('todas');
  const [refreshKey, setRefreshKey] = useState(0);
  const [pagoSeleccionado, setPagoSeleccionado] = useState<PagoRead | null>(null);
  const [mostrarManual, setMostrarManual] = useState(false);
  const [pagoAReasignar, setPagoAReasignar] = useState<PagoRead | null>(null);
  const [reasignando, setReasignando] = useState(false);
  const [reasignarError, setReasignarError] = useState<string | null>(null);
  // Aviso de pagos procesados automáticamente (auto-distribución FIFO) en
  // los últimos 7 días — para que el tesorero se entere sin revisar el correo.
  const [autoRecientes, setAutoRecientes] =
    useState<AutoRecientesCount | null>(null);

  useEffect(() => {
    getPagosAutoRecientesCount(7)
      .then(setAutoRecientes)
      .catch(() => setAutoRecientes(null));
  }, [refreshKey]);

  // Debounce simple del input search (300ms) para no spamear backend.
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    listPagos({
      estado: estadoTab,
      search: searchDebounced || undefined,
    })
      .then((data) => {
        if (!cancelado) setPagos(data);
      })
      .catch((err) => {
        if (cancelado) return;
        const mensaje =
          err instanceof ApiError
            ? `Error ${err.status} al cargar pagos`
            : 'Error de red';
        setError(mensaje);
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [estadoTab, searchDebounced, refreshKey]);

  // Filtro client-side por tipo (casa identificada / sin identificar / todas).
  // Solo aplica en la pestaña "Por distribuir" — en "Aprobados" todos los
  // pagos tienen casa.
  const pagosFiltrados = useMemo(() => {
    if (estadoTab === 'aprobado') return pagos;
    if (tipo === 'casa_identificada') return pagos.filter((p) => p.lote_id !== null);
    if (tipo === 'sin_identificar') return pagos.filter((p) => p.lote_id === null);
    return pagos;
  }, [pagos, tipo, estadoTab]);

  // Limite de seguridad en render: el tab "Aprobados" puede traer cientos de
  // pagos (carga inicial = ~367). Renderizar todos congelaria el browser.
  // El tesorero usa el search para encontrar el pago a reasignar, no
  // recorre el listado completo.
  const LIMITE_RENDER = 50;
  const pagosVisibles = pagosFiltrados.slice(0, LIMITE_RENDER);
  const truncado = pagosFiltrados.length > LIMITE_RENDER;

  // KPIs del listado.
  const stats: StatItem[] = useMemo(() => {
    const total = pagos.reduce((acc, p) => acc + p.monto_total, 0);
    if (estadoTab === 'aprobado') {
      return [
        {
          label: 'Aprobados',
          value: <Monto value={total} />,
          meta: `${pagos.length} transferencia${pagos.length === 1 ? '' : 's'}`,
          valueColor: 'success',
        },
        {
          label: 'Cantidad',
          value: <Num>{pagos.length}</Num>,
          meta: 'Aplicados a casas',
        },
      ];
    }
    const conLote = pagos.filter((p) => p.lote_id !== null).length;
    const sinLote = pagos.length - conLote;
    return [
      {
        label: 'Por distribuir',
        value: <Monto value={total} />,
        meta: `${pagos.length} transferencia${pagos.length === 1 ? '' : 's'}`,
        valueColor: 'warning',
      },
      {
        label: 'Cantidad',
        value: <Num>{pagos.length}</Num>,
        meta: 'En cola',
      },
      {
        label: 'Casa identificada',
        value: <Num>{conLote}</Num>,
        meta: 'Auto-detectado parcial',
        valueColor: 'success',
      },
      {
        label: 'Sin identificar',
        value: <Num>{sinLote}</Num>,
        meta: 'Requiere atención',
        valueColor: 'orange',
      },
    ];
  }, [pagos, estadoTab]);

  function handleClasificar(pago: PagoRead) {
    setPagoSeleccionado(pago);
  }

  function handleSolicitarReasignar(pago: PagoRead) {
    setReasignarError(null);
    setPagoAReasignar(pago);
  }

  async function handleConfirmarReasignar() {
    if (!pagoAReasignar) return;
    setReasignando(true);
    setReasignarError(null);
    try {
      await revertirAprobacionPago(pagoAReasignar.id);
      setPagoAReasignar(null);
      setEstadoTab('por_distribuir');
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const mensaje =
        err instanceof ApiError
          ? `Error ${err.status} al revertir aprobación`
          : 'Error de red';
      setReasignarError(mensaje);
    } finally {
      setReasignando(false);
    }
  }

  function handleModalClasificado() {
    setPagoSeleccionado(null);
    setRefreshKey((k) => k + 1);
  }

  function handlePagoManualCreado() {
    setMostrarManual(false);
    setEstadoTab('por_distribuir');
    setRefreshKey((k) => k + 1);
  }

  const titulo = estadoTab === 'aprobado' ? 'Pagos aprobados' : 'Pagos por distribuir';
  const subtitulo =
    estadoTab === 'aprobado'
      ? 'Transferencias ya aplicadas. Reasigna solo si fueron clasificadas a la casa equivocada.'
      : 'Transferencias recibidas pendientes de aplicar a cuotas o ingresos.';

  return (
    <div className="px-8 py-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-wide text-orange font-medium">
            Tesorería
          </div>
          <h1 className="text-2xl font-medium text-text-primary mt-1">{titulo}</h1>
          <p className="text-sm text-text-secondary mt-1">{subtitulo}</p>
        </div>
        <Button
          variant="primary"
          size="sm"
          type="button"
          onClick={() => setMostrarManual(true)}
          className="shrink-0"
        >
          + Registrar pago manual
        </Button>
      </div>

      <div className="flex gap-2 border-b border-white/10">
        <TabEstado
          activo={!mostrarCorreos && estadoTab === 'por_distribuir'}
          onClick={() => {
            setMostrarCorreos(false);
            setEstadoTab('por_distribuir');
          }}
          label="Por distribuir"
        />
        <TabEstado
          activo={!mostrarCorreos && estadoTab === 'aprobado'}
          onClick={() => {
            setMostrarCorreos(false);
            setEstadoTab('aprobado');
          }}
          label="Aprobados"
        />
        <TabEstado
          activo={mostrarCorreos}
          onClick={() => setMostrarCorreos(true)}
          label="Correos del banco"
        />
      </div>

      {mostrarCorreos && <CorreosBancoTab />}

      {!mostrarCorreos && (
        <>
      {estadoTab === 'por_distribuir' &&
        autoRecientes &&
        autoRecientes.count > 0 && (
          <div className="bg-orange/10 border border-orange/30 rounded-md px-4 py-3 text-sm text-text-primary flex items-center justify-between gap-3">
            <span>
              ✓ {autoRecientes.count} pago
              {autoRecientes.count === 1 ? '' : 's'} se{' '}
              {autoRecientes.count === 1 ? 'procesó' : 'procesaron'}{' '}
              automáticamente en los últimos {autoRecientes.dias} días.
            </span>
            <button
              type="button"
              onClick={() => setEstadoTab('aprobado')}
              className="text-orange hover:underline whitespace-nowrap font-medium"
            >
              Verlos →
            </button>
          </div>
        )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <Estadistica key={s.label} item={s} />
        ))}
      </div>

      <div className="flex gap-3 flex-wrap items-center">
        <input
          type="text"
          aria-label="Buscar por remitente o número de operación"
          placeholder="Buscar por remitente o N° operación…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] py-2 px-3 bg-bg-sunken border-[0.5px] border-white/15 rounded-sm text-text-primary placeholder:text-text-muted focus:border-orange focus:ring-1 focus:ring-orange/30 outline-none"
        />
        {estadoTab === 'por_distribuir' && (
          <>
            <span className="text-sm text-text-secondary">Filtrar:</span>
            <ChipFiltro
              activo={tipo === 'todas'}
              onClick={() => setTipo('todas')}
              label="Todas"
            />
            <ChipFiltro
              activo={tipo === 'casa_identificada'}
              onClick={() => setTipo('casa_identificada')}
              label="Casa identificada"
            />
            <ChipFiltro
              activo={tipo === 'sin_identificar'}
              onClick={() => setTipo('sin_identificar')}
              label="Sin identificar"
            />
          </>
        )}
      </div>

      {loading && (
        <p className="text-sm text-text-muted">Cargando pagos…</p>
      )}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}
      {!loading && !error && pagosFiltrados.length === 0 && (
        <p className="text-sm text-text-muted">
          {pagos.length === 0
            ? estadoTab === 'aprobado'
              ? 'No hay pagos aprobados.'
              : 'No hay pagos pendientes de clasificar.'
            : 'Ningún pago coincide con el filtro seleccionado.'}
        </p>
      )}
      {!loading && !error && pagosFiltrados.length > 0 && (
        <div>
          {pagosVisibles.map((p) => (
            <FilaPago
              key={p.id}
              pago={p}
              modo={estadoTab === 'aprobado' ? 'reasignar' : 'clasificar'}
              onAccion={
                estadoTab === 'aprobado'
                  ? handleSolicitarReasignar
                  : handleClasificar
              }
            />
          ))}
          {truncado && (
            <div className="text-sm text-text-muted py-3 text-center border-[0.5px] border-white/[0.08] rounded-md mt-2">
              Mostrando {LIMITE_RENDER} de {pagosFiltrados.length} pagos. Usa
              la búsqueda para acotar.
            </div>
          )}
        </div>
      )}
        </>
      )}

      {pagoSeleccionado && (
        <ModalClasificar
          pago={pagoSeleccionado}
          onCerrar={() => setPagoSeleccionado(null)}
          onClasificado={handleModalClasificado}
        />
      )}

      {mostrarManual && (
        <ModalRegistrarPagoManual
          onClose={() => setMostrarManual(false)}
          onSuccess={handlePagoManualCreado}
        />
      )}

      {pagoAReasignar && (
        <ModalConfirmarReasignar
          pago={pagoAReasignar}
          loading={reasignando}
          error={reasignarError}
          onCerrar={() => {
            if (!reasignando) setPagoAReasignar(null);
          }}
          onConfirmar={handleConfirmarReasignar}
        />
      )}
    </div>
  );
}

interface TabEstadoProps {
  activo: boolean;
  onClick: () => void;
  label: string;
}

function TabEstado({ activo, onClick, label }: TabEstadoProps) {
  const className = activo
    ? 'px-4 py-2 border-b-2 border-orange text-orange font-medium cursor-pointer -mb-px'
    : 'px-4 py-2 border-b-2 border-transparent text-text-secondary cursor-pointer hover:text-text-primary -mb-px';
  return (
    <button
      type="button"
      role="tab"
      aria-selected={activo}
      className={className}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

interface ModalConfirmarReasignarProps {
  pago: PagoRead;
  loading: boolean;
  error: string | null;
  onCerrar: () => void;
  onConfirmar: () => void;
}

function ModalConfirmarReasignar({
  pago,
  loading,
  error,
  onCerrar,
  onConfirmar,
}: ModalConfirmarReasignarProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-reasignar-titulo"
    >
      <div className="bg-bg-card border border-white/10 rounded-md p-6 max-w-md w-full space-y-4">
        <h2
          id="modal-reasignar-titulo"
          className="text-lg font-medium text-text-primary"
        >
          ¿Reasignar este pago?
        </h2>
        <div className="space-y-2 text-sm text-text-secondary">
          <p>
            El pago volverá a "Por distribuir" y deberás clasificarlo manualmente
            a la casa correcta. Las distribuciones actuales se borrarán y el
            saldo de las cuotas afectadas se recalculará.
          </p>
          <div className="bg-bg-sunken border-[0.5px] border-white/[0.08] rounded-sm p-3 space-y-1">
            <div className="text-text-primary font-medium">
              +<Monto value={pago.monto_total} />
            </div>
            <div className="text-xs text-text-muted">
              Remitente: {pago.remitente_nombre}
            </div>
            <div className="text-xs text-text-muted">
              {pago.email_template_detectado} ·{' '}
              {formatFecha(pago.fecha_transferencia)}
            </div>
          </div>
        </div>
        {error && (
          <div className="bg-state-danger/15 border-l-4 border-state-danger p-2 rounded-sm text-sm text-text-primary">
            {error}
          </div>
        )}
        <div className="flex gap-2 justify-end pt-2">
          <Button
            variant="ghost"
            size="md"
            onClick={onCerrar}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="danger"
            size="md"
            onClick={onConfirmar}
            disabled={loading}
          >
            {loading ? 'Reasignando…' : 'Sí, reasignar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface ChipFiltroProps {
  activo: boolean;
  onClick: () => void;
  label: string;
}

function ChipFiltro({ activo, onClick, label }: ChipFiltroProps) {
  const className = activo
    ? 'px-3 py-1 bg-orange/15 text-orange rounded-full text-sm font-medium cursor-pointer'
    : 'px-3 py-1 bg-bg-sunken text-text-secondary rounded-full text-sm cursor-pointer hover:text-text-primary';
  return (
    <button type="button" className={className} onClick={onClick} aria-pressed={activo}>
      {label}
    </button>
  );
}
